import React, { useState, useEffect } from "react"
import { Card, Spin, Modal, message } from "antd"
import { useHistory } from "react-router-dom"
import { apiClient } from "../../../api"
import { token, CheckTokenExp } from "../../../config"
import "./BuyCredit.scoped.scss"

// Function to refresh credit balance (calls api/credit/get_credit_byuser)
const refreshCreditBalance = async () => {
  try {
    const req = JSON.stringify({})
    await apiClient.post("api/credit/get_credit_byuser", req, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-type": "application/json; charset=UTF-8"
      }
    })
    // Dispatch custom event to notify header/parent to refresh
    window.dispatchEvent(new CustomEvent("creditUpdated"))
  } catch (error) {
    console.error("Error refreshing credit balance:", error)
  }
}

export default function BuyCredit() {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [slipFile, setSlipFile] = useState(null)
  const [slipPreview, setSlipPreview] = useState(null)
  const [errorModal, setErrorModal] = useState({
    visible: false,
    title: "",
    message: ""
  })
  const [successModal, setSuccessModal] = useState({
    visible: false,
    data: null
  })

  useEffect(() => {
    if (CheckTokenExp()) {
      window.location.href = "logout"
      return
    }
  }, [])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        message.error("Please upload image files only")
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error("File size must not exceed 5MB")
        return
      }
      setSlipFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setSlipPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        message.error("Please upload image files only")
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error("File size must not exceed 5MB")
        return
      }
      setSlipFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setSlipPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeSlip = () => {
    setSlipFile(null)
    setSlipPreview(null)
  }

  const handleSubmit = async () => {
    if (!slipFile) {
      setErrorModal({
        visible: true,
        title: "Please upload slip",
        message: "Please upload a transfer slip image"
      })
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()
      formData.append("file", slipFile)

      const response = await apiClient.post("api/buycredit/verify-slip", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      })

      const result = response.data

      // API returns Pascal case (Status, Message, Description, Data)
      const status = result.Status || result.status
      const resultMessage = result.Message || result.message
      const description = result.Description || result.description
      const data = result.Data || result.data

      if (status === 200) {
        setSuccessModal({
          visible: true,
          data: normalizeData(data)
        })
      } else {
        setErrorModal({
          visible: true,
          title: resultMessage || "Error",
          message: description || "Please try again"
        })
      }
    } catch (error) {
      console.error("Error verifying slip:", error)
      const errorData = error.response?.data
      const errorTitle = errorData?.Message || errorData?.message || "Error"
      const errorDesc = errorData?.Description || errorData?.description || "Unable to connect to server"
      setErrorModal({
        visible: true,
        title: errorTitle,
        message: errorDesc
      })
    } finally {
      setUploading(false)
    }
  }

  const formatNumber = (value) => {
    if (value === undefined || value === null) {
      return "0.00"
    }
    return Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Format date to DD/MM/YYYY HH:mm
  const formatDate = (dateString) => {
    if (!dateString) return "-"
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, "0")
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, "0")
      const minutes = String(date.getMinutes()).padStart(2, "0")
      return `${day}/${month}/${year} ${hours}:${minutes}`
    } catch {
      return dateString
    }
  }

  // Helper to normalize API response data (Pascal case to camelCase)
  const normalizeData = (data) => {
    if (!data) return null
    return {
      transRef: data.TransRef || data.transRef,
      amount: data.Amount || data.amount,
      credit: data.Credit || data.credit,
      date: data.Date || data.date,
      sender: data.Sender || data.sender ? {
        bankName: (data.Sender || data.sender)?.BankName || (data.Sender || data.sender)?.bankName,
        bankShort: (data.Sender || data.sender)?.BankShort || (data.Sender || data.sender)?.bankShort,
        accountName: (data.Sender || data.sender)?.AccountName || (data.Sender || data.sender)?.accountName,
        accountNumber: (data.Sender || data.sender)?.AccountNumber || (data.Sender || data.sender)?.accountNumber
      } : null,
      receiver: data.Receiver || data.receiver ? {
        bankName: (data.Receiver || data.receiver)?.BankName || (data.Receiver || data.receiver)?.bankName,
        accountName: (data.Receiver || data.receiver)?.AccountName || (data.Receiver || data.receiver)?.accountName
      } : null
    }
  }

  const closeErrorModal = () => {
    setErrorModal({ visible: false, title: "", message: "" })
  }

  const closeSuccessModal = async () => {
    setSuccessModal({ visible: false, data: null })
    // Clear form
    setSlipFile(null)
    setSlipPreview(null)
    // Refresh credit balance
    await refreshCreditBalance()
  }

  return (
    <div className="buy-credit-container">
      <div className="buy-credit-header">
        <h2>Confirm Payment</h2>
      </div>

      <Card className="buy-credit-card">


        {/* Slip Upload Section */}
        <div className="slip-upload-section">
          <h3>Upload transfer slip</h3>

          {!slipPreview ? (
            <div
              className="dropzone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById("slip-input").click()}
            >
              <div className="dropzone-content">
                <div className="dropzone-icon">📷</div>
                <p>Drag and drop files here, or click to select files</p>
                <span>Supported formats: JPG, PNG (max 5MB)</span>
              </div>
              <input
                id="slip-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          ) : (
            <div className="slip-preview">
              <img src={slipPreview} alt="Slip Preview" />
              <button className="remove-slip" onClick={removeSlip}>
                ✕ Delete file
              </button>
            </div>
          )}
        </div>

        {/* Buy Button */}
        <div className="button-wrapper">
          <button
            className={`buy-button ${uploading ? "loading" : ""}`}
            onClick={handleSubmit}
            disabled={uploading || !slipFile}
          >
            {uploading ? (
              <>
                <Spin size="small" style={{ marginRight: 8 }} />
                <span>Verifying...</span>
              </>
            ) : (
              "Buy now"
            )}
          </button>
        </div>

        {/* Bank Remark Section */}
        <div className="bank-remark-section">
          <p className="remark-title"><strong>Note:</strong> Recommend using slip from: Kasikornbank (K-Bank), Siam Commercial Bank (SCB), TMBThanachart Bank (ttb)</p>
        </div>
      </Card>

      {/* Error Modal */}
      <Modal
        visible={errorModal.visible}
        onOk={closeErrorModal}
        onCancel={closeErrorModal}
        okText="OK"
        cancelButtonProps={{ style: { display: "none" } }}
        width={420}
        centered
        closable={false}
        bodyStyle={{ padding: "40px 32px 24px" }}
        footer={
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <button
              onClick={closeErrorModal}
              style={{
                minWidth: 140,
                height: 44,
                borderRadius: 8,
                border: "none",
                background: "#1a1a2e",
                color: "white",
                fontSize: 15,
                fontWeight: 500,
                cursor: "pointer"
              }}
            >
              OK
            </button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <div style={{ marginBottom: 20 }}>
            <svg viewBox="0 0 52 52" width="64" height="64">
              <circle cx="26" cy="26" r="25" fill="none" stroke="#ef4444" strokeWidth="2" />
              <path fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" d="M18 18l16 16M34 18l-16 16" />
            </svg>
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: "#1a1a2e", marginBottom: 12 }}>{errorModal.title}</h2>
          <p style={{ fontSize: 15, color: "#6c757d", lineHeight: 1.6, margin: 0, maxWidth: 320 }}>{errorModal.message}</p>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        visible={successModal.visible}
        onOk={closeSuccessModal}
        onCancel={closeSuccessModal}
        okText="OK"
        cancelButtonProps={{ style: { display: "none" } }}
        width={420}
        centered
        closable={false}
        bodyStyle={{ padding: "40px 32px 24px" }}
        footer={
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <button
              onClick={closeSuccessModal}
              style={{
                minWidth: 140,
                height: 44,
                borderRadius: 8,
                border: "none",
                background: "#1a1a2e",
                color: "white",
                fontSize: 15,
                fontWeight: 500,
                cursor: "pointer"
              }}
            >
              OK
            </button>
          </div>
        }
      >
        {successModal.data && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <div style={{ marginBottom: 20 }}>
              <svg viewBox="0 0 52 52" width="64" height="64">
                <circle cx="26" cy="26" r="25" fill="none" stroke="#22c55e" strokeWidth="2" />
                <path fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M14 27l7 7 16-16" />
              </svg>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: "#1a1a2e", marginBottom: 28 }}>Transfer successful</h2>

            <div style={{ width: "100%", background: "#f8f9fa", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid #e9ecef" }}>
                <span style={{ fontSize: 14, color: "#6c757d" }}>Transaction Ref</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: "#212529", marginLeft: 16, wordBreak: "break-all", textAlign: "right" }}>{successModal.data.transRef || "-"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid #e9ecef" }}>
                <span style={{ fontSize: 14, color: "#6c757d" }}>Amount</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: "#212529", marginLeft: 16 }}>{formatNumber(successModal.data.amount)} THB</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", margin: "12px -24px", background: "#e8f5e9", borderRadius: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: "#2e7d32" }}>Credit Received</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: "#22c55e" }}>{formatNumber(successModal.data.credit)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0" }}>
                <span style={{ fontSize: 14, color: "#6c757d" }}>Date</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: "#212529", marginLeft: 16 }}>{formatDate(successModal.data.date)}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
